// Minimal declarative wrappers around the Google Maps JavaScript API. Marker and
// info window content is rendered by React through portals, so markers can use
// the same Tailwind/DaisyUI styling as the rest of the app.
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import { LatLngLiteral } from '@/components/LiveMap/geo';
import {
  GoogleMapsLibraries,
  loadGoogleMaps,
} from '@/components/LiveMap/googleMapsLoader';

import { GOOGLE_MAPS_MAP_ID } from '@/env';

type MapContextValue = {
  map: google.maps.Map;
  libraries: GoogleMapsLibraries;
};

const MapContext = createContext<MapContextValue | null>(null);

/**
 * Access the map instance from a child of `<GoogleMap>`.
 * @returns {MapContextValue}
 */
export function useGoogleMap(): MapContextValue {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useGoogleMap must be used inside <GoogleMap>');
  }
  return context;
}

// Continental US, used until the first fitBounds
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const DEFAULT_ZOOM = 4;

/**
 * Load the Maps API and render a map that fills its parent.
 * @param {object} props
 * @param {React.ReactNode} props.children - Markers, polylines, etc.
 * @param {() => void} [props.onClick] - Click on an empty spot of the map.
 * @returns {React.JSX.Element}
 */
export function GoogleMap({
  children,
  onClick,
}: {
  children?: React.ReactNode;
  onClick?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [context, setContext] = useState<MapContextValue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    loadGoogleMaps()
      .then((libraries) => {
        if (isCancelled || !containerRef.current) return;
        const map = new libraries.maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: GOOGLE_MAPS_MAP_ID,
          clickableIcons: false,
          gestureHandling: 'greedy',
          streetViewControl: false,
          fullscreenControl: false,
          // On phones the Map/Satellite switch collides with the overlay controls
          mapTypeControl: window.innerWidth >= 768,
          mapTypeControlOptions: {
            position: google.maps.ControlPosition.TOP_RIGHT,
          },
        });
        setContext({ map, libraries });
      })
      .catch((loadError: Error) => {
        if (!isCancelled) setError(loadError.message);
      });
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!context || !onClick) return;
    const listener = context.map.addListener('click', onClick);
    return () => listener.remove();
  }, [context, onClick]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {!!error && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div role="alert" className="alert alert-error max-w-md">
            {`The map could not be loaded: ${error}`}
          </div>
        </div>
      )}
      {!!context && (
        <MapContext.Provider value={context}>{children}</MapContext.Provider>
      )}
    </div>
  );
}

/**
 * Advanced Marker whose content is rendered by React.
 * @param {object} props
 * @param {LatLngLiteral} props.position
 * @param {number} [props.zIndex]
 * @param {string} [props.title] - Accessible name / native tooltip.
 * @param {() => void} [props.onClick]
 * @param {React.ReactNode} props.children - Marker content.
 * @returns {React.ReactPortal}
 */
export function AdvancedMarker({
  position,
  zIndex,
  title,
  onClick,
  children,
}: {
  position: LatLngLiteral;
  zIndex?: number;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { map, libraries } = useGoogleMap();
  const [content] = useState(() => document.createElement('div'));
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );

  useEffect(() => {
    const marker = new libraries.marker.AdvancedMarkerElement({
      map,
      content,
      position,
      zIndex,
      title,
      gmpClickable: !!onClick,
    });
    markerRef.current = marker;
    return () => {
      marker.map = null;
      markerRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (markerRef.current) markerRef.current.position = position;
  }, [position.lat, position.lng]);

  useEffect(() => {
    if (markerRef.current) markerRef.current.zIndex = zIndex ?? null;
  }, [zIndex]);

  // Keep the native tooltip current (e.g. a truck's heading or staleness)
  useEffect(() => {
    if (markerRef.current) markerRef.current.title = title ?? '';
  }, [title]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !onClick) return;
    marker.gmpClickable = true;
    const listener = marker.addListener('click', onClick);
    return () => listener.remove();
  }, [onClick]);

  return createPortal(children, content);
}

/**
 * Polyline that keeps its path and style in sync with props.
 * @param {object} props
 * @param {LatLngLiteral[]} props.path
 * @param {google.maps.PolylineOptions} [props.options] - Stroke, icons, zIndex, etc.
 * @returns {null}
 */
export function Polyline({
  path,
  options,
}: {
  path: LatLngLiteral[];
  options?: google.maps.PolylineOptions;
}) {
  const { map, libraries } = useGoogleMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    const polyline = new libraries.maps.Polyline({ map });
    polylineRef.current = polyline;
    return () => {
      polyline.setMap(null);
      polylineRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    polylineRef.current?.setOptions({ ...options, path });
  }, [path, options]);

  return null;
}

/**
 * Polygon, used to draw site geofences that were drawn by hand in react-app.
 * @param {object} props
 * @param {LatLngLiteral[]} props.path
 * @param {google.maps.PolygonOptions} [props.options]
 * @returns {null}
 */
export function Polygon({
  path,
  options,
}: {
  path: LatLngLiteral[];
  options?: google.maps.PolygonOptions;
}) {
  const { map, libraries } = useGoogleMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    const polygon = new libraries.maps.Polygon({ map, clickable: false });
    polygonRef.current = polygon;
    return () => {
      polygon.setMap(null);
      polygonRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    polygonRef.current?.setOptions({ ...options, paths: path });
  }, [path, options]);

  return null;
}

/**
 * Circle, used to draw site geofences.
 * @param {object} props
 * @param {LatLngLiteral} props.center
 * @param {number} props.radius - Meters.
 * @param {google.maps.CircleOptions} [props.options]
 * @returns {null}
 */
export function Circle({
  center,
  radius,
  options,
}: {
  center: LatLngLiteral;
  radius: number;
  options?: google.maps.CircleOptions;
}) {
  const { map, libraries } = useGoogleMap();
  const circleRef = useRef<google.maps.Circle | null>(null);

  useEffect(() => {
    const circle = new libraries.maps.Circle({ map, clickable: false });
    circleRef.current = circle;
    return () => {
      circle.setMap(null);
      circleRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    circleRef.current?.setOptions({ ...options, center, radius });
  }, [center.lat, center.lng, radius, options]);

  return null;
}

/**
 * Info window pinned to a position, with React content.
 * @param {object} props
 * @param {LatLngLiteral} props.position
 * @param {() => void} props.onClose - Called when the user closes the window.
 * @param {number} [props.offsetY] - Pixels to lift the window above the position (marker height).
 * @param {React.ReactNode} props.children
 * @returns {React.ReactPortal}
 */
export function InfoWindow({
  position,
  onClose,
  offsetY = 0,
  children,
}: {
  position: LatLngLiteral;
  onClose: () => void;
  offsetY?: number;
  children: React.ReactNode;
}) {
  const { map, libraries } = useGoogleMap();
  const [content] = useState(() => document.createElement('div'));
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const infoWindow = new libraries.maps.InfoWindow({
      content,
      position,
      pixelOffset: new google.maps.Size(0, -offsetY),
      maxWidth: 320,
    });
    infoWindow.open({ map, shouldFocus: false });
    infoWindowRef.current = infoWindow;
    const listener = infoWindow.addListener('closeclick', () =>
      onCloseRef.current()
    );
    return () => {
      listener.remove();
      infoWindow.close();
      infoWindowRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    infoWindowRef.current?.setPosition(position);
  }, [position.lat, position.lng]);

  // Follow the marker's height (e.g. a site switching between pin and tag)
  useEffect(() => {
    infoWindowRef.current?.setOptions({
      pixelOffset: new google.maps.Size(0, -offsetY),
    });
  }, [offsetY]);

  return createPortal(children, content);
}
